import { motion } from "framer-motion";
import { Play, ArrowUpRight } from "lucide-react";
import { scrollToSection } from "@/components/Header";

const PROJECTS = [
  {
    type: "video",
    src: "/assets/project-video.mp4",
    poster: "/assets/project-video-poster.webp",
    caption: "On The Job — Watch",
    span: "md:col-span-2 md:row-span-2",
  },
  { type: "photo", src: "/assets/project-kitchen-sink.webp", caption: "Kitchen Renovation — Sink & Tapware", span: "" },
  { type: "photo", src: "/assets/project-external-drainage.webp", caption: "External Drainage & Downpipes", span: "" },
  { type: "photo", src: "/assets/project-salon-fitoff.webp", caption: "Hair Salon Plumbing Fit-Off", span: "" },
  { type: "photo", src: "/assets/project-stack-roughin.webp", caption: "New Build Drainage Rough-In", span: "" },
  { type: "photo", src: "/assets/project-copper-roughin.webp", caption: "Copper Rough-In — Renovation", span: "" },
  { type: "photo", src: "/assets/project-heat-pump.webp", caption: "Heat Pump Hot Water Install", span: "" },
  { type: "photo", src: "/assets/project-drainage.webp", caption: "Drainage Excavation & Repair", span: "" },
  { type: "photo", src: "/assets/project-gas-cooktop.webp", caption: "Gas Cooktop Installation", span: "" },
  { type: "photo", src: "/assets/project-water-tank.webp", caption: "Water Tank & Hot Water Install", span: "" },
];

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 },
  }),
};

export default function Projects() {
  return (
    <section id="projects" data-testid="projects-section" className="border-b border-[#111111]/15">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14 md:mb-20">
          <motion.h2
            variants={reveal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            className="font-display uppercase tracking-wide leading-none text-5xl md:text-7xl"
          >
            Our Most Recent<br />
            <span className="text-[#003BFF]">Projects</span>
          </motion.h2>
          <motion.p
            variants={reveal}
            custom={1}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            className="max-w-sm text-base text-[#555555] font-medium leading-relaxed"
          >
            Fresh from the field — photos and videos from our latest jobs across
            Melbourne's north.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#111111]/15 border border-[#111111]/15">
          {PROJECTS.map((p, i) => (
            <motion.figure
              key={p.src}
              custom={i}
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              data-testid={`project-${p.type}-${i}`}
              data-media-slot={p.type}
              className={`${p.span} group relative bg-neutral-300 overflow-hidden ${
                p.type === "video" ? "aspect-[3/4] md:aspect-auto" : "aspect-[3/4]"
              }`}
            >
              {p.type === "video" ? (
                <video
                  src={p.src}
                  poster={p.poster}
                  controls
                  muted
                  playsInline
                  preload="metadata"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <img
                  src={p.src}
                  alt={p.caption}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              )}
              <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#111111]/85 to-transparent px-5 pt-10 pb-4 flex items-end justify-between pointer-events-none">
                <span className="text-white text-xs font-bold uppercase tracking-[0.2em]">
                  {p.caption}
                </span>
                {p.type === "video" && (
                  <span className="w-9 h-9 rounded-full bg-white/90 flex items-center justify-center">
                    <Play className="w-4 h-4 text-[#111111] fill-[#111111] ml-0.5" />
                  </span>
                )}
              </figcaption>
            </motion.figure>
          ))}

          <motion.button
            custom={PROJECTS.length}
            variants={reveal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            data-testid="projects-cta-tile"
            onClick={() => scrollToSection("#contact")}
            className="group relative aspect-[3/4] md:aspect-auto md:col-span-2 md:min-h-[300px] bg-[#003BFF] hover:bg-[#111111] transition-colors duration-500 flex flex-col items-start justify-between p-8 text-left"
          >
            <ArrowUpRight className="w-8 h-8 text-white transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            <span className="font-display uppercase tracking-wide text-white text-2xl md:text-3xl leading-tight">
              Your Project<br />Could Be Next
            </span>
          </motion.button>
        </div>
      </div>
    </section>
  );
}
